import { DbTransaction } from '@ionic-enterprise/secure-storage'
import { SecureStorageService } from './secureStorage.service'
import { IStoragePcr } from './pcrDocs.service'
import Logger from './logger.service'

export class NowSQLiteDbService extends SecureStorageService {
  pcrDocsTableName = 'PCRDocs'

  _sqlReady: Promise<void>

  constructor(database: string) {
    super(database)

    this._sqlReady = this.initializeNowDb()
  }

  private async initializeNowDb(): Promise<void> {
    await this.ready()

    await this.handle?.transaction((tx) => {
      this.createTables(tx)
    })
  }

  private createTables(tx: DbTransaction): void {
    this.createPCRDocsTable(tx)
  }

  private createPCRDocsTable(tx: DbTransaction) {
    const id = { name: 'id', type: 'INTEGER PRIMARY KEY' }
    const createdBy = { name: 'createdBy', type: 'TEXT' }
    const createDateTime = { name: 'createDateTime', type: 'INTEGER' }
    const data = { name: 'data', type: 'TEXT' }

    this.runSQL(
      tx,
      this.createTableSQL(`${this.pcrDocsTableName}`, [id, createdBy, createDateTime, data]),
      undefined,
      true
    )
    this.runSQL(tx, this.createTableIndexSQL(`${this.pcrDocsTableName}`), undefined, true)
  }

  async getAllPCRDocs(table: string): Promise<Array<IStoragePcr>> {
    return this.getAll(table)
  }

  async addPCRDoc(storagePCR: IStoragePcr): Promise<IStoragePcr> {
    await this._sqlReady

    const createdBy = storagePCR.createdBy

    const countBefore = await this.getPcrCountByCreatedBy(this.pcrDocsTableName, createdBy)
    Logger.info(`Number of PCRs createdBy [${createdBy}] before add = ${countBefore}`)

    const added = await this.handle?.transaction((tx) => {
      this.runSQL(
        tx,
        `INSERT INTO ${this.pcrDocsTableName} (id, createdBy, createDateTime, data) VALUES (?, ?, ?, ?)`,
        [+storagePCR.pcrId, createdBy, storagePCR.createDateTime, storagePCR.data],
        true
      )
    })

    const countAfter = await this.getPcrCountByCreatedBy(this.pcrDocsTableName, createdBy)
    Logger.info(`Number of PCRs createdBy [${createdBy}] after add = ${countAfter}`)

    if (countAfter !== countBefore + 1) {
      Logger.info(
        `Add PCR - Counts don't match as expected: countBefore = [${countBefore}] and countAfter = [${countAfter}]`
      )
    }

    return added
  }

  async updatePCRDoc(storagePCR: IStoragePcr): Promise<string | number> {
    await this._sqlReady

    await this.handle?.transaction((tx) => {
      this.runSQL(tx, `UPDATE ${this.pcrDocsTableName} SET data = ? WHERE id = ${+storagePCR.pcrId}`, [storagePCR.data])
    })

    // We need to explicity return this since 'this.handle?.transaction' does not return a value
    return storagePCR.pcrId
  }

  async removePCRDoc(pcrId: string | number, createdBy: string): Promise<void> {
    const countBefore = await this.getPcrCountByCreatedBy(this.pcrDocsTableName, createdBy)
    Logger.info(`Number of PCRs createdBy [${createdBy}] before remove = ${countBefore}`)

    this.remove(pcrId, this.pcrDocsTableName)

    const countAfter = await this.getPcrCountByCreatedBy(this.pcrDocsTableName, createdBy)
    Logger.info(`Number of PCRs createdBy [${createdBy}] after remove = ${countAfter}`)

    if (countAfter !== countBefore - 1) {
      Logger.info(
        `Remove PCR - Counts don't match as expected: countBefore = [${countBefore}] and countAfter = [${countAfter}]`
      )
    }
  }
}
